
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { MEMBERSHIP_PROVIDERS } from '@/utils/constants';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';

interface FlatDiscount {
	provider: string;
	promo: MemberPromo | PunchhPromo;
}

@Component<AvailableDiscounts>({
	components: {
		StandardModal,
		InfoBanner
	}
})
export default class AvailableDiscounts extends Vue {
	@Prop({ type: Array, required: true }) private providers!: MembershipProviderGroup[];
	@Prop({ type: Array, default: () => [] }) private selectedPromoIds!: string[];
	@Prop({ type: String, default: '' }) private lockedProvider!: string;
	@Prop({ type: Boolean, default: false }) private loading!: boolean;
	@Prop({ type: Boolean, default: false }) private readOnly!: boolean;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;

	private localSelectedIds: string[] = [...this.selectedPromoIds];

	/**
	 * All providers' promos flattened into a single selectable list.
	 *
	 * @return {FlatDiscount[]}
	 */
	private get discounts(): FlatDiscount[] {
		return this.providers.reduce((acc: FlatDiscount[], group) => {
			// Once a provider is locked in for the check, only its discounts are offered
			if (this.lockedProvider && group.provider !== this.lockedProvider) {
				return acc;
			}
			(group.promos as (MemberPromo | PunchhPromo)[]).forEach(promo => acc.push({ provider: group.provider, promo }));
			return acc;
		}, []);
	}

	/**
	 * Whether a final-once-added discount (ELB / Summer Pass — anything but Punchh)
	 * is in the shown list, which surfaces the finality notice. League Bowler/Summer
	 * Pass only apply at Bowlero locations, so the notice is gated to them.
	 *
	 * @return {boolean}
	 */
	private get showFinalityNotice(): boolean {
		const isBowlero = this.restaurant.group === 'bowlero' || !!this.restaurant.groups?.includes('bowlero');
		return isBowlero && this.discounts.some(item => item.provider !== MEMBERSHIP_PROVIDERS.PUNCHH);
	}

	/**
	 * Whether the confirm button is disabled. Always disabled when read-only; otherwise
	 * enabled when there's a selection to apply or a previously-applied discount to clear.
	 *
	 * @return {boolean}
	 */
	private get disableConfirm(): boolean {
		return this.readOnly || !(this.localSelectedIds.length || this.selectedPromoIds.length);
	}

	/**
	 * Title to show for a discount card. Some providers (e.g. ELB) don't supply a
	 * name and put the label in the description, so we fall back to it to keep a
	 * consistent bold title across providers.
	 *
	 * @param {MemberPromo | PunchhPromo} promo
	 * @return {string}
	 */
	private promoTitle(promo: MemberPromo | PunchhPromo): string {
		return promo.name || promo.description || '';
	}

	/**
	 * Whether the given discount is currently selected.
	 *
	 * @param {MemberPromo | PunchhPromo} promo
	 * @return {boolean}
	 */
	private isSelected(promo: MemberPromo | PunchhPromo): boolean {
		return this.localSelectedIds.includes(String(promo.id));
	}

	/**
	 * Select a discount. Only one discount can be applied at a time, so this
	 * replaces any existing selection (or clears it when tapping the selected one).
	 *
	 * @param {FlatDiscount} item
	 * @return {void}
	 */
	private toggle(item: FlatDiscount): void {
		this.localSelectedIds = this.isSelected(item.promo) ? [] : [String(item.promo.id)];
	}

	/**
	 * Emit the selected discount (one at most) for the parent to apply. When the
	 * selection is cleared, the locked provider is still emitted so the parent can
	 * re-apply it with no promos and drop the discount.
	 *
	 * @return {void}
	 */
	private applyDiscounts(): void {
		const selected = this.discounts.filter(item => this.localSelectedIds.includes(String(item.promo.id)));
		this.$emit('apply', {
			provider: selected.length ? selected[0].provider : this.lockedProvider,
			promos: selected.map(item => item.promo)
		});
	}
}
